import * as THREE from 'three';

// Values the page drives; the scene eases toward them every frame.
export type VentureTargets = {
  pointerX: number; // -1 … 1, hover look
  pointerY: number;
  dragYaw: number; // radians, accumulated by dragging
  dragPitch: number;
  entered: number; // 0 at the gate, 1 once inside
  progress: number; // 0 … 1 through the environments
  leaving: number; // 0 … 1 while handing over to the page
};

type Environment = { image: string; fallback: string; face?: 'light'; wide?: boolean };

// A wide panorama spans this much of the horizon (radians) and this much vertically
const WIDE_SPAN = 4.2;
const WIDE_HEIGHT = 2.0;
// How far the camera slowly turns while passing through each environment (radians, ~50°)
const TURN_PER_SCENE = 0.88;

// Camera yaw that looks at horizontal texture position u (0 … 1) on the inside of the sphere
const yawForU = (u: number) => {
  const phi = u * Math.PI * 2;
  return Math.atan2(-Math.cos(phi), -Math.sin(phi));
};

// Finds the brightest stretch of sky near the horizon — the sun, or the glow where it set
async function findLightU(blob: Blob): Promise<number | null> {
  try {
    const bmp = await createImageBitmap(blob, { resizeWidth: 128, resizeHeight: 64 });
    const c = document.createElement('canvas');
    c.width = 128;
    c.height = 64;
    const g = c.getContext('2d') as CanvasRenderingContext2D;
    g.drawImage(bmp, 0, 0);
    const { data } = g.getImageData(0, 18, 128, 16); // just above the horizon
    const cols = new Float32Array(128);
    for (let y = 0; y < 16; y++) {
      for (let x = 0; x < 128; x++) {
        const i = (y * 128 + x) * 4;
        const lum = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
        cols[x] += lum * lum; // favour the brightest highlights
      }
    }
    let best = 0;
    let bestX = 0;
    for (let x = 0; x < 128; x++) {
      let sum = 0;
      for (let k = -4; k <= 4; k++) sum += cols[(x + k + 128) % 128];
      if (sum > best) {
        best = sum;
        bestX = x;
      }
    }
    return (bestX + 0.5) / 128;
  } catch {
    return null;
  }
}

type SceneConfig = {
  gate: Environment;
  scenes: Environment[];
};

const RADIUS = 500;

const damp = (a: number, b: number, lambda: number, dt: number) => a + (b - a) * (1 - Math.exp(-lambda * dt));
const smooth = (a: number, b: number, v: number) => {
  const t = Math.min(1, Math.max(0, (v - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

function glowTexture() {
  const c = document.createElement('canvas');
  c.width = c.height = 256;
  const g = c.getContext('2d') as CanvasRenderingContext2D;
  const grd = g.createRadialGradient(128, 128, 0, 128, 128, 128);
  grd.addColorStop(0, 'rgba(255,238,210,1)');
  grd.addColorStop(1, 'rgba(255,200,150,0)');
  g.fillStyle = grd;
  g.fillRect(0, 0, 256, 256);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

// Loads a panorama, decoding and resizing it off the main thread so nothing stutters mid-journey
async function loadPanorama(
  url: string,
  maxWidth: number,
  opts: { wide?: boolean; findLight?: boolean } = {},
): Promise<{ tex: THREE.Texture; lightU: number | null }> {
  const res = await fetch(url, { mode: 'cors' });
  if (!res.ok) throw new Error(`Could not load ${url}`);
  const blob = await res.blob();
  const lightU = opts.findLight ? await findLightU(blob) : null;

  let source: ImageBitmap | HTMLCanvasElement;
  let flipped = false;
  try {
    source = await createImageBitmap(
      blob,
      opts.wide
        ? { imageOrientation: 'flipY' } // wide stills keep their own size and aspect
        : { resizeWidth: maxWidth, resizeHeight: maxWidth / 2, resizeQuality: 'high', imageOrientation: 'flipY' },
    );
    flipped = true;
  } catch {
    // Older browsers: decode, then draw into a capped canvas
    const img = new Image();
    img.decoding = 'async';
    img.src = URL.createObjectURL(blob);
    await img.decode();
    const w = Math.min(maxWidth, img.naturalWidth);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = opts.wide ? Math.round((w * img.naturalHeight) / img.naturalWidth) : Math.round(w / 2);
    (canvas.getContext('2d') as CanvasRenderingContext2D).drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(img.src);
    source = canvas;
  }

  const tex = new THREE.Texture(source);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.flipY = !flipped;
  // Panoramas are viewed near 1:1, so mipmaps add upload cost without benefit
  tex.generateMipmaps = false;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return { tex, lightU };
}

export function createVentureScene(canvas: HTMLCanvasElement, targets: VentureTargets, config: SceneConfig) {
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setClearColor(0x050505, 1);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 2000);
  camera.rotation.order = 'YXZ';

  const small = Math.min(window.innerWidth, window.innerHeight) < 700;
  const maxWidth = Math.min(renderer.capabilities.maxTextureSize, small ? 4096 : 6144);
  const anisotropy = Math.min(4, renderer.capabilities.getMaxAnisotropy());
  const tmpAhead = new THREE.Vector3();

  const sphere = new THREE.SphereGeometry(RADIUS, 128, 64);
  sphere.scale(-1, 1, 1); // view from inside without mirroring
  // A partial sphere for wide (non-360°) panoramas, centred straight ahead at yaw 0
  const wideSphere = new THREE.SphereGeometry(
    RADIUS,
    96,
    48,
    Math.PI * 1.5 - WIDE_SPAN / 2,
    WIDE_SPAN,
    Math.PI / 2 - WIDE_HEIGHT / 2,
    WIDE_HEIGHT,
  );
  wideSphere.scale(-1, 1, 1);
  const disposables: { dispose(): void }[] = [sphere, wideSphere];

  type Layer = {
    mesh: THREE.Mesh;
    mat: THREE.MeshBasicMaterial;
    env: Environment;
    loaded: number;
    ready: boolean;
    requested: boolean;
    faceYaw: number;
  };

  const makeLayer = (env: Environment, order: number, faceYaw: number): Layer => {
    // Slightly graded down for a cinematic, low-key look
    const mat = new THREE.MeshBasicMaterial({ color: 0xe2ddd6, transparent: true, opacity: 0, depthWrite: false, depthTest: false });
    const mesh = new THREE.Mesh(env.wide ? wideSphere : sphere, mat);
    mesh.renderOrder = order;
    mesh.visible = false;
    scene.add(mesh);
    disposables.push(mat);
    return { mesh, mat, env, loaded: 0, ready: false, requested: false, faceYaw: env.wide ? 0 : faceYaw };
  };

  let disposed = false;
  const load = async (layer: Layer) => {
    if (layer.requested) return;
    layer.requested = true;
    const opts = { wide: layer.env.wide, findLight: layer.env.face === 'light' };
    let result: { tex: THREE.Texture; lightU: number | null } | null = null;
    try {
      result = await loadPanorama(layer.env.image, maxWidth, opts);
    } catch {
      // A flat fallback still is not a true panorama, so keep the default framing
      result = await loadPanorama(layer.env.fallback, maxWidth, { wide: layer.env.wide }).catch(() => null);
    }
    if (!result || disposed) return;
    const tex = result.tex;
    if (result.lightU !== null) layer.faceYaw = yawForU(result.lightU);
    tex.anisotropy = anisotropy;
    // Upload to the GPU now, while the scene is still out of sight, instead of on first view
    renderer.initTexture(tex);
    layer.mat.map = tex;
    layer.mat.needsUpdate = true;
    layer.ready = true;
    disposables.push(tex);
  };

  // Default framing for each environment matches the slow turn the journey always had
  const sceneLayers = config.scenes.map((env, i) => makeLayer(env, i, -(i / config.scenes.length) * Math.PI * 1.2));
  const gateLayer = makeLayer(config.gate, config.scenes.length + 1, 0);

  // Load the gate, then every environment one after another, well before each is reached
  (async () => {
    await load(gateLayer);
    for (const layer of sceneLayers) {
      if (disposed) return;
      await load(layer);
    }
  })();
  const request = (layer: Layer) => {
    void load(layer);
  };

  // Compile shaders up front so the first fade does not hitch
  renderer.compile(scene, camera);

  // Fine dust hanging in the air, for depth against the far environment
  const dustCount = 700;
  const positions = new Float32Array(dustCount * 3);
  for (let i = 0; i < dustCount; i++) {
    const r = 4 + Math.random() * 30;
    const a = Math.random() * Math.PI * 2;
    positions[i * 3] = Math.cos(a) * r;
    positions[i * 3 + 1] = (Math.random() - 0.5) * 16;
    positions[i * 3 + 2] = Math.sin(a) * r;
  }
  const dustGeo = new THREE.BufferGeometry();
  dustGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  const glow = glowTexture();
  const dustMat = new THREE.PointsMaterial({ size: 0.08, map: glow, color: 0xf1dfbf, transparent: true, opacity: 0.4, depthWrite: false, depthTest: false, blending: THREE.AdditiveBlending });
  const dust = new THREE.Points(dustGeo, dustMat);
  dust.renderOrder = 50;
  scene.add(dust);
  disposables.push(dustGeo, dustMat, glow);

  // The light that blooms at the hand-over
  const bloomMat = new THREE.SpriteMaterial({ map: glow, transparent: true, opacity: 0, depthWrite: false, depthTest: false, blending: THREE.AdditiveBlending });
  const bloom = new THREE.Sprite(bloomMat);
  bloom.renderOrder = 60;
  scene.add(bloom);
  disposables.push(bloomMat);

  let baseFov = 75;
  const resize = () => {
    const w = canvas.clientWidth || window.innerWidth;
    const h = canvas.clientHeight || window.innerHeight;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    // Wide, unhurried framing — never a tight crop
    baseFov = w / h < 1 ? 92 : 75;
    camera.updateProjectionMatrix();
  };
  resize();
  window.addEventListener('resize', resize);

  const cur = { px: 0, py: 0, yaw: 0, pitch: 0, entered: 0, progress: 0, leaving: 0, limit: 100, base: 0 };
  const n = config.scenes.length;
  let last = performance.now();
  let time = 0;
  let frame = 0;

  const tick = (now: number) => {
    const dt = Math.min((now - last) / 1000, 0.05);
    last = now;
    time += dt;

    cur.px = damp(cur.px, targets.pointerX, 2, dt);
    cur.py = damp(cur.py, targets.pointerY, 2, dt);
    cur.yaw = damp(cur.yaw, targets.dragYaw, 4, dt);
    cur.pitch = damp(cur.pitch, targets.dragPitch, 4, dt);
    cur.entered = damp(cur.entered, targets.entered, 1.4, dt);
    cur.progress = damp(cur.progress, targets.progress, 1.5, dt);
    cur.leaving = damp(cur.leaving, targets.leaving, 1.2, dt);

    const e = cur.entered;
    const s = cur.progress * n;

    // One continuous, unbroken camera turn through the whole journey. Instead of swinging the
    // camera at each cut, every environment is rotated so its best view (the sunset, the
    // skyline) sits exactly where the camera is looking at the middle of its chapter.
    const idx = Math.max(0, Math.min(n - 1, Math.floor(s + 0.45)));
    sceneLayers.forEach((l, i) => {
      l.mesh.rotation.y = -(i + 0.5) * TURN_PER_SCENE - l.faceYaw;
    });
    const journeyTurn = -s * TURN_PER_SCENE;

    // Look around: drag to turn freely, hover to lean
    const wide = !!sceneLayers[idx].env.wide && e > 0.5;
    const gateDrift = time * 0.025 * (1 - e);
    camera.rotation.y = journeyTurn + cur.yaw - cur.px * 0.14 - gateDrift;
    const pitchLimit = wide ? 0.15 : 0.6;
    camera.rotation.x = Math.max(-pitchLimit, Math.min(pitchLimit, cur.pitch - cur.py * 0.07));

    // Load each environment shortly before it is needed
    sceneLayers.forEach((l, i) => {
      if (s > i - 1.4) request(l);
    });
    [...sceneLayers, gateLayer].forEach((l) => {
      if (l.ready) l.loaded = damp(l.loaded, 1, 2.5, dt);
    });

    gateLayer.mat.opacity = gateLayer.loaded * (1 - smooth(0.45, 0.95, e));
    gateLayer.mesh.visible = gateLayer.mat.opacity > 0.001;

    sceneLayers.forEach((l, i) => {
      // Long, soft crossfades: each environment dissolves in across half a chapter
      const fadeIn = i === 0 ? smooth(0.35, 0.9, e) : smooth(i - 0.45, i + 0.05, s);
      l.mat.opacity = l.loaded * fadeIn;
      const covered = i < n - 1 && s > i + 1.08 && (sceneLayers[i + 1]?.loaded ?? 0) > 0.98;
      l.mesh.visible = l.mat.opacity > 0.001 && !covered;
    });

    // A gentle breath into each environment, and a step forward through the gate
    // (continuous across cuts, so the frame never jumps between environments)
    const breath = Math.sin(Math.PI * 2 * s) * 1.5;
    camera.fov = baseFov - breath - Math.sin(Math.PI * Math.min(e, 1)) * 14 + cur.leaving * 20;
    camera.updateProjectionMatrix();

    dust.rotation.y = time * 0.01 + cur.progress * 2;
    dustMat.opacity = 0.4 * (1 - cur.leaving);

    // Keep the bloom straight ahead of the viewer
    tmpAhead.set(0, 0, -20).applyEuler(camera.rotation);
    bloom.position.copy(tmpAhead);
    bloom.scale.setScalar(10 + cur.leaving * 120);
    bloomMat.opacity = cur.leaving;

    renderer.render(scene, camera);
    frame = requestAnimationFrame(tick);
  };
  frame = requestAnimationFrame(tick);

  return {
    dispose() {
      disposed = true;
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', resize);
      disposables.forEach((d) => d.dispose());
      renderer.dispose();
    },
  };
}
